import { Box } from "@chakra-ui/react";
import NoResults from "components/elements/NoResults";
import moment from "moment";
import { LoansContext } from "providers/loans";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useEffect, useState } from "react";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import renderLegend from "./legend";
import renderTooltip from "./tooltip";
import colors from "utils/colors";
import { shortLoanAmount } from "utils/helpers";

const DATE_FORMAT = "MMM YY";

const AmountStatusBar = () => {
  const { loans } = useContext(LoansContext);

  const { user } = useTenant();

  const [data, setData] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);

  const custom_currency_data =
    user &&
    user?.company &&
    user?.company?.company_configurations?.[0] &&
    user?.company?.company_configurations?.[0]?.custom_currency &&
    user?.company?.company_configurations?.[0]?.custom_currency?.currency;

  const custom_currency =
    user?.company?.company_configurations?.[0]?.custom_currency?.currency || "$";

  useEffect(() => {
    const loansArray = loans as any[];
    const widgetStatuses = user.company.statuses.filter(
      (status: any) => status.isDraft || status.isSubmitted || status.isFunded
    );

    const months: {
      [key: string]: any;
    } = {};

    loansArray.forEach(loan => {
      if (moment(loan.funding_date).isValid()) {
        months[moment(loan.funding_date).format(DATE_FORMAT)] = {
          ...widgetStatuses.reduce((a: any, v: any) => ({ ...a, [v.label]: 0 }), {}),
          Total: 0,
        };
      }
    });
    loansArray.forEach(loan => {
      if (moment(loan.funding_date).isValid()) {
        const formattedFundingDate = moment(loan.funding_date).format(DATE_FORMAT);

        if (widgetStatuses.find((status: any) => status.id === loan.status.id)) {
          months[formattedFundingDate][loan.status.label] =
            months[formattedFundingDate][loan.status.label] + loan.amount / 1000000;
        }

        months[formattedFundingDate].Total =
          months[formattedFundingDate].Total + loan.amount / 1000000;
      }
    });
    const chartData: any[] = [];
    Object.keys(months).forEach(month => {
      const monthData: {
        [key: string]: string | number;
      } = {
        name: month,
      };

      Object.keys(months[month]).forEach(status => {
        monthData[status] = Math.round(months[month][status] * 10) / 10;
      }); //
      chartData.push(monthData);
    });
    setStatuses(widgetStatuses);
    setData(
      chartData.sort(
        (a, b) =>
          new Date(moment(a.name, DATE_FORMAT).toDate()).getTime() -
          new Date(moment(b.name, DATE_FORMAT).toDate()).getTime()
      )
    );
  }, [loans]);

  return (
    <Box h="300px">
      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            barCategoryGap={10}
            width={500}
            height={340}
            data={data}
            margin={{
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            }}
          >
            <CartesianGrid vertical={false} stroke="#f5f5f5" />
            {statuses.map((status, index) => (
              <XAxis
                key={status.id}
                dataKey="name"
                xAxisId={status.label}
                axisLine={false}
                tickLine={false}
                hide={index !== 0}
                tick={{
                  fontSize: "10px",
                  color: colors.GRAY_300,
                }}
              />
            ))}
            <YAxis
              tickFormatter={value => `${custom_currency_data ? custom_currency : "$"}${value}M`}
              domain={[
                0,
                data.reduce((prev, current) => (prev.Total > current.Total ? prev : current))
                  .Total +
                  data.reduce((prev, current) => (prev.Total > current.Total ? prev : current))
                    .Total *
                    0.1,
              ]}
              width={48}
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: "10px",
                color: colors.GRAY_300,
              }}
            />
            <Tooltip content={props => renderTooltip(props, { user: user })} />
            <Legend content={renderLegend} />
            {statuses.reverse().map((status, index) => (
              <Bar
                key={status.id}
                dataKey={status.label}
                xAxisId={status.label}
                fill={status.color}
                opacity={0.5}
                radius={[15, 15, 0, 0]}
              >
                {data.length < 13 && (
                  <LabelList
                    dataKey={status.label}
                    fontSize="12px"
                    fontWeight="700"
                    fill="white"
                    formatter={(value: number) =>
                      value ? shortLoanAmount(value.toString(), custom_currency) : ``
                    }
                    offset={-20}
                    position="top"
                  />
                )}
              </Bar>
            ))}
            <Line type="monotone" xAxisId={statuses[0].label} dataKey="Total" stroke="#FC2E53" />
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <NoResults message="Not enough data yet..." />
      )}
    </Box>
  );
};

export default AmountStatusBar;
